(function () {
  const selectors = {
    drawer: '[data-cart-drawer]',
    panel: '.wl-cart-drawer__panel',
    open: '[data-cart-drawer-open]',
    close: '[data-cart-drawer-close]',
    items: '[data-cart-drawer-items]',
    footer: '[data-cart-drawer-footer]',
    subtotal: '[data-cart-drawer-subtotal]',
    quantityChange: '[data-cart-quantity-change]',
    countText: '[data-cart-count-text]',
  };

  class CartDrawer {
    constructor(drawer) {
      this.drawer = drawer;
      this.panel = drawer.querySelector(selectors.panel);
      this.items = drawer.querySelector(selectors.items);
      this.footer = drawer.querySelector(selectors.footer);
      this.subtotal = drawer.querySelector(selectors.subtotal);
      this.currencyCode = drawer.dataset.currencyCode || 'USD';
      this.previousFocus = null;
      this.isOpen = false;

      document.addEventListener('click', this.onDocumentClick.bind(this));
      document.addEventListener('submit', this.onDocumentSubmit.bind(this));
      document.addEventListener('keydown', this.onKeydown.bind(this));
      drawer.addEventListener('click', this.onDrawerClick.bind(this));
    }

    async onDocumentClick(event) {
      const opener = event.target.closest(selectors.open);
      if (!opener) return;

      event.preventDefault();
      await this.open(opener);
    }

    async onDocumentSubmit(event) {
      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      event.preventDefault();

      const submitter = event.submitter || form.querySelector('[type="submit"]');
      if (submitter) submitter.disabled = true;

      try {
        await this.addFormToCart(form);
        await this.open(submitter || form);
      } catch (error) {
        form.submit();
      } finally {
        if (submitter) submitter.disabled = false;
      }
    }

    async addFormToCart(form) {
      const action = this.shopifyUpdateCartAction();
      const line = this.cartLineFromForm(form);

      if (action && line) {
        try {
          const result = await action(
            { lines: [line] },
            { event: { context: 'wl-cart-drawer' } }
          );
          this.throwCartUserErrors(result);
          return result;
        } catch (error) {
          // Fall through to the legacy AJAX endpoint if Shopify's action layer is not ready.
        }
      }

      return this.addFormToCartWithAjax(form);
    }

    async addFormToCartWithAjax(form) {
      const response = await fetch(`${this.routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (!response.ok) throw new Error('Add to cart failed');
      return response.json().catch(() => ({}));
    }

    cartLineFromForm(form) {
      const formData = new FormData(form);
      const merchandiseId = String(formData.get('id') || '').trim();
      if (!merchandiseId) return null;

      const quantity = Math.max(1, Number(formData.get('quantity') || 1) || 1);
      const sellingPlanId = String(formData.get('selling_plan') || '').trim();
      const attributes = [];

      formData.forEach((value, key) => {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (!match) return;
        attributes.push({ key: match[1], value: String(value) });
      });

      return {
        merchandiseId,
        quantity,
        ...(sellingPlanId ? { sellingPlanId } : {}),
        ...(attributes.length ? { attributes } : {}),
      };
    }

    shopifyUpdateCartAction() {
      const action = window.Shopify?.actions?.updateCart;
      return typeof action === 'function' ? action : null;
    }

    throwCartUserErrors(result) {
      const errors = Array.isArray(result?.userErrors) ? result.userErrors : [];
      if (!errors.length) return;
      throw new Error(errors.map((error) => error.message).filter(Boolean).join(', ') || 'Add to cart failed');
    }

    async onDrawerClick(event) {
      if (event.target.closest(selectors.close)) {
        this.close();
        return;
      }

      const quantityButton = event.target.closest(selectors.quantityChange);
      if (!quantityButton) return;

      event.preventDefault();
      const quantity = Number(quantityButton.dataset.quantity);
      const key = quantityButton.dataset.lineKey;

      if (!key || !Number.isFinite(quantity)) return;

      quantityButton.disabled = true;
      await this.changeQuantity(key, Math.max(quantity, 0));
      quantityButton.disabled = false;
    }

    onKeydown(event) {
      if (!this.isOpen || event.key !== 'Escape') return;
      this.close();
    }

    async open(opener) {
      this.previousFocus = opener || document.activeElement;
      this.drawer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('wl-cart-drawer-lock');

      window.requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.panel.focus();
      });

      this.isOpen = true;
      await this.refresh();
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('wl-cart-drawer-lock');
      this.isOpen = false;

      window.setTimeout(() => {
        if (!this.isOpen) this.drawer.hidden = true;
      }, 240);

      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus();
      }
    }

    async refresh() {
      try {
        const cart = await this.fetchCart();
        this.render(cart);
      } catch (error) {
        this.items.innerHTML = '<div class="wl-cart-drawer__empty"><p>Cart unavailable.</p></div>';
      }
    }

    async changeQuantity(key, quantity) {
      try {
        const response = await fetch(`${this.routesRoot()}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: key, quantity }),
        });

        if (!response.ok) throw new Error('Cart update failed');

        const cart = await response.json();
        this.render(cart);
      } catch (error) {
        await this.refresh();
      }
    }

    async fetchCart() {
      const response = await fetch(`${this.routesRoot()}cart.js`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    }

    render(cart) {
      this.currencyCode = cart.currency || this.currencyCode;
      this.updateCount(cart.item_count || 0);

      if (!cart.items || cart.items.length === 0) {
        this.items.innerHTML = this.emptyMarkup();
        if (this.footer) this.footer.hidden = true;
        return;
      }

      this.items.innerHTML = cart.items.map((item) => this.itemMarkup(item)).join('');
      if (this.footer) this.footer.hidden = false;
      if (this.subtotal) this.subtotal.textContent = this.formatMoney(cart.total_price);
    }

    updateCount(count) {
      document.querySelectorAll(selectors.countText).forEach((node) => {
        node.textContent = count > 0 ? ` ${count}` : '';
      });
    }

    itemMarkup(item) {
      const image = item.image
        ? `<img src="${this.escapeAttribute(this.resizeImage(item.image, '420x'))}" alt="" loading="lazy">`
        : '';
      const variant = item.variant_title ? `<p>${this.escapeHtml(item.variant_title)}</p>` : '';
      const properties = this.propertiesMarkup(item.properties);
      const quantity = Number(item.quantity) || 0;

      return `
        <article class="wl-cart-drawer-item" data-cart-drawer-item data-line-key="${this.escapeAttribute(item.key)}">
          <a class="wl-cart-drawer-item__media" href="${this.escapeAttribute(item.url)}">${image}</a>
          <div class="wl-cart-drawer-item__detail">
            <a class="wl-cart-drawer-item__title" href="${this.escapeAttribute(item.url)}">${this.escapeHtml(item.product_title || item.title)}</a>
            ${variant}
            ${properties}
            <span>${this.formatMoney(item.final_line_price)}</span>
            <div class="wl-cart-drawer-item__quantity" aria-label="Quantity">
              <button type="button" data-cart-quantity-change data-line-key="${this.escapeAttribute(item.key)}" data-quantity="${quantity - 1}">-</button>
              <span>${quantity}</span>
              <button type="button" data-cart-quantity-change data-line-key="${this.escapeAttribute(item.key)}" data-quantity="${quantity + 1}">+</button>
            </div>
          </div>
        </article>
      `;
    }

    propertiesMarkup(properties) {
      if (!properties || Array.isArray(properties)) return '';

      return Object.entries(properties)
        .filter(([key, value]) => value && !key.startsWith('_'))
        .map(([key, value]) => `<p>${this.escapeHtml(key)}: ${this.escapeHtml(String(value))}</p>`)
        .join('');
    }

    emptyMarkup() {
      return `
        <div class="wl-cart-drawer__empty">
          <p>Your bag is empty.</p>
          <a href="/collections/all" data-cart-drawer-close>Continue Shopping</a>
        </div>
      `;
    }

    resizeImage(url, size) {
      if (!url || !size) return url;
      return url.replace(/(\.[^.?]+)(\?.*)?$/, `_${size}$1$2`);
    }

    formatMoney(cents) {
      const amount = Number(cents) / 100;
      if (!Number.isFinite(amount)) return '';

      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.currencyCode,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    }

    routesRoot() {
      return window.Shopify?.routes?.root || '/';
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value || '';
      return div.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/"/g, '&quot;');
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.querySelector(selectors.drawer);
    if (drawer) new CartDrawer(drawer);
  });
})();
